import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Controller, type FieldPath, type FieldPathByValue, useFormContext } from 'react-hook-form';

import { useDebounce } from 'hooks/useDebaunce';

import { IContract, IProject, useProjectTreeStore } from 'store/projectContract.store';

import { FILTER_REFERENCES_TYPE_KEYS, FilterReferenceKey } from 'api/queries/getFilterItemOptionsByRowType.query';
import { FilterItemReference } from 'api/queries/getPoapViewById.query';
import { ROW_TYPE_KEYS, RowTypeKey } from 'api/queries/getRowTypeOptions.query';

import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import { TreeCrumbs } from 'components/common/TreeCrumbs/TreeCrumbs';

import { Alert } from 'components/DesignSystem/Alert';
import { Button } from 'components/DesignSystem/Button';
import { Select } from 'components/DesignSystem/Select';
import { TextInput } from 'components/DesignSystem/TextInput';
import { UseViewsManagerProvider } from 'components/pages/ScheduleViewsPage/ViewsManager/ViewsManager.provider';
import { ViewRowsFormValues } from 'components/pages/ScheduleViewsPage/components/ViewConfiguration/ViewConfiguration.types';
import classes from 'components/pages/ScheduleViewsPage/components/ViewConfiguration/components/ViewConfigurationDrawerForm/components/RowConfig/RowConfig.module.scss';
import { useFilterItemReferencesSnapshot } from 'components/pages/ScheduleViewsPage/components/ViewConfiguration/components/ViewConfigurationDrawerForm/components/RowConfig/hooks/useFilterItemReferencesSnapshot';
import { useGetLatestCompareToVersions } from 'components/pages/ScheduleViewsPage/components/ViewConfiguration/components/ViewConfigurationDrawerForm/components/RowConfig/hooks/useGetLatestCompareToVersions';

export type RowConfigMode = 'add' | 'edit';

export interface FormValues {
    project: IProject | undefined;
    contract: IContract | undefined;
    rowName: string;
    rowType: RowTypeKey | undefined;
    filterItem: FilterReferenceKey | undefined;
    filterItemReferences: FilterItemReference[];
    latestVersionId?: number;
    compareVersionId?: number;
    /** Set when row is loaded from API; omitted for new rows. Sent in save payload for existing rows. */
    rowId?: number;
}

const MIN_ACTIVITIES_FOR_WARNING = 15;

interface Props {
    api: UseViewsManagerProvider['api'];
    mode: RowConfigMode;
    rowIndex: number;
    onConfirm: () => void;
    onCancel: () => void;
    onDelete?: () => void;
}

function rowDataPath(rowIndex: number) {
    return `rows.${rowIndex}.data` as FieldPath<ViewRowsFormValues>;
}

function rowFieldName<K extends keyof FormValues>(
    rowIndex: number,
    field: K,
): FieldPathByValue<ViewRowsFormValues, FormValues[K]> {
    return `rows.${rowIndex}.data.${field}` as unknown as FieldPathByValue<ViewRowsFormValues, FormValues[K]>;
}

export const RowConfig = ({ api, mode, rowIndex, onConfirm, onCancel, onDelete }: Props) => {
    const { control, setValue, watch, trigger, getValues } = useFormContext<ViewRowsFormValues>();
    const { update: updateProjectTreeStore } = useProjectTreeStore();
    const name = useCallback(<K extends keyof FormValues>(field: K) => rowFieldName(rowIndex, field), [rowIndex]);

    const [searchActivity, setSearchActivity] = useState(null);
    const debouncedSearchActivity = useDebounce<string | null>(searchActivity, 1000);

    const row = watch(rowDataPath(rowIndex)) as FormValues;
    const rowTypeValueFromForm = row.rowType;
    const filterItemValueFromForm = row.filterItem;
    const contractValueFromForm = row.contract;
    const projectValueFromForm = row.project;
    const filterItemReferencesValueFromForm = row.filterItemReferences;

    const activitiesFromReferencesCount = filterItemReferencesValueFromForm.reduce(
        (acc, { activitiesCount }) => acc + (activitiesCount ?? 0),
        0,
    );

    const filterItemReferencesSnapshot = useFilterItemReferencesSnapshot({ rowIndex, row });

    const { compareVersionId, latestVersionId } = useGetLatestCompareToVersions({
        projectId: projectValueFromForm?.id,
        contractId: contractValueFromForm?.id,
    });

    useEffect(() => {
        setValue(name('latestVersionId'), latestVersionId);
        setValue(name('compareVersionId'), compareVersionId);
    }, [latestVersionId, compareVersionId, name, setValue]);

    const programGeneralHierrarchyListData = api.getProgramGeneralHierrarchyList.instance.data;
    const rowTypeOptionsData = api.getRowTypeOptions.instance.data;

    const filterItemOptionsData = api.getFilterItemOptions.instance({ rowType: rowTypeValueFromForm }).data;
    const { data: activitiesOptionsData, isFetching: isFetchingGetFilterReferencesOptions } =
        api.getFilterReferencesOptions.instance({
            rowType: rowTypeValueFromForm,
            contractId: contractValueFromForm?.id,
            metaDataId: latestVersionId,
            filterItem: filterItemValueFromForm,
            searchToken: debouncedSearchActivity,
            filterItemReferences: filterItemReferencesSnapshot,
        });
    const clearFilterItem = () => {
        setValue(name('filterItem'), undefined);
        setValue(name('filterItemReferences'), []);
    };

    const clearFilterItemReferences = () => {
        setValue(name('filterItemReferences'), []);
    };

    const onProjectContractSelectionFinish = ({ project, contract }: { project: IProject; contract: IContract }) => {
        // Update local form state
        setValue(name('project'), project);
        setValue(name('contract'), contract);

        // Clear dependent fields
        clearFilterItemReferences();
    };

    const rowTypeOptions: ISelectOption<RowTypeKey>[] = useMemo(() => {
        return rowTypeOptionsData?.map((item) => ({ label: item.value, value: item.key })) || [];
    }, [rowTypeOptionsData]);
    const filterItemOptions = useMemo(() => {
        return filterItemOptionsData?.map((item) => ({ label: item.value, value: item.key })) || [];
    }, [filterItemOptionsData]);
    const activitiesOptions = useMemo(() => {
        return (
            activitiesOptionsData?.value?.map((item) => ({
                label: item.label + (item.brokenReferenceLink ? ' [⚠️Broken reference link]' : ''),
                value: item.value,
                brokenReferenceLink: item.brokenReferenceLink,
                activitiesCount: item?.activitiesCount ?? undefined,
            })) || []
        );
    }, [activitiesOptionsData]);

    const onInputChange = (inputText, event) => {
        if (event.action !== 'input-blur' && event.action !== 'menu-close' && event.action !== 'set-value') {
            setSearchActivity(inputText);
        }
        if (event.action === 'menu-close') {
            setSearchActivity(null);
        }
    };

    const handleDelete = () => {
        if (mode === 'edit') {
            onDelete?.();
        } else {
            onCancel();
        }
    };

    const handleConfirmClick = async () => {
        const isValidRow = await trigger([
            name('rowName'),
            name('rowType'),
            name('filterItem'),
            name('filterItemReferences'),
        ]);

        if (isValidRow) {
            //update global project store
            const [project, contract] = getValues([name('project'), name('contract')]);
            if (project && contract) {
                updateProjectTreeStore({ project, contract });
            }

            onConfirm();
        }
    };
    const icon = rowTypeValueFromForm === ROW_TYPE_KEYS.SUMMARY_BAR ? 'align_horizontal_left' : 'flag';

    return (
        <div className={classes.RowConfig}>
            <div className={classes.formWrapper}>
                <div className={classes.rowNameRow}>
                    <span className={classes.dragIconWrapper} aria-hidden>
                        <MuiIcon icon={'drag_indicator'} fontSize={'2rem'} type={'outlined'} />
                    </span>
                    <div className={classes.rowNameIconsLeft}>
                        <span className={classes.rowNameIcon} aria-hidden>
                            <MuiIcon icon={icon} fontSize={'2rem'} color={'var(--text-quinary)'} type={'outlined'} />
                        </span>
                    </div>
                    <Controller
                        name={name('rowName')}
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextInput
                                {...field}
                                data-testid={'row-config-row-name'}
                                className={classes.rowNameInput}
                                placeholder={'Row name'}
                                clearable={false}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                onChange={(e) => {
                                    setValue(name('rowName'), e.target.value, {
                                        shouldValidate: true,
                                        shouldDirty: true,
                                        shouldTouch: true,
                                    });
                                }}
                            />
                        )}
                        rules={{
                            required: 'Row name is required',
                            validate: (value) => (value?.trim()?.length > 0 ? true : 'Row name is required'),
                        }}
                    />
                    <button
                        type={'button'}
                        className={classes.rowNameDelete}
                        data-testid={'row-config-delete-row'}
                        onClick={handleDelete}
                        aria-label={mode === 'edit' ? 'Delete row' : 'Cancel add'}
                    >
                        <MuiIcon
                            icon={'delete'}
                            fontSize={'2rem'}
                            color={'var(--button-primary-bg_destructive)'}
                            type={'outlined'}
                        />
                    </button>
                </div>

                {programGeneralHierrarchyListData && contractValueFromForm?.uuid && (
                    <TreeCrumbs
                        selectedNode={contractValueFromForm.uuid}
                        data={programGeneralHierrarchyListData}
                        onSelectionFinish={onProjectContractSelectionFinish}
                    />
                )}

                <div className={classes.row}>
                    <div className={classes.inputWrapper}>
                        <label htmlFor={'rowType'}>Row type</label>
                        <Controller
                            name={name('rowType')}
                            control={control}
                            rules={{
                                required: 'Row type is required',
                                validate: (value) => (value != null ? true : 'Row type is required'),
                            }}
                            render={({ field, fieldState }) => (
                                <Select<ISelectOption<RowTypeKey>>
                                    {...field}
                                    data-testid={'row-config-row-type'}
                                    options={rowTypeOptions}
                                    value={rowTypeOptions.find((item) => item.value === rowTypeValueFromForm) || null}
                                    className={classes.selectFullWidth}
                                    onChange={(newValue) => {
                                        setValue(name('rowType'), newValue ? newValue.value : undefined, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                            shouldTouch: true,
                                        });
                                        clearFilterItem();
                                    }}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                    </div>
                    <div className={classes.inputWrapper}>
                        <label htmlFor={'filterItem'}>Select activities by</label>
                        <Controller
                            name={name('filterItem')}
                            control={control}
                            rules={{
                                validate: (value) => (value != null ? true : 'Select activities by is required'),
                            }}
                            render={({ field, fieldState }) => (
                                <Select<ISelectOption<FilterReferenceKey>>
                                    {...field}
                                    data-testid={'row-config-select-activities-by'}
                                    value={
                                        filterItemOptions.find((item) => item.value === filterItemValueFromForm) || null
                                    }
                                    onChange={(newValue) => {
                                        setValue(name('filterItem'), newValue ? newValue.value : undefined, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                            shouldTouch: true,
                                        });
                                        clearFilterItemReferences();
                                    }}
                                    options={filterItemOptions}
                                    className={classes.selectFullWidth}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                    </div>
                </div>
                <div className={classes.row}>
                    <div className={classes.inputWrapper}>
                        <label htmlFor={'filterItemReferences'}>Activity selection</label>
                        <Controller
                            name={name('filterItemReferences')}
                            control={control}
                            rules={{
                                validate: (value) =>
                                    Array.isArray(value) && value.length > 0 ? true : 'Select at least one activity',
                            }}
                            render={({ field, fieldState }) => {
                                const isMulti =
                                    filterItemValueFromForm !== FILTER_REFERENCES_TYPE_KEYS.WBS &&
                                    filterItemValueFromForm !== FILTER_REFERENCES_TYPE_KEYS.RESOURCE;
                                return (
                                    <Select<ISelectOption<string>>
                                        {...field}
                                        data-testid={'row-config-activity-selection'}
                                        options={activitiesOptions}
                                        className={classes.selectFullWidth}
                                        onInputChange={onInputChange}
                                        isMulti={isMulti}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        onChange={(newValue) => {
                                            const next = Array.isArray(newValue)
                                                ? newValue
                                                : newValue
                                                  ? [newValue]
                                                  : [];
                                            setValue(name('filterItemReferences'), next, {
                                                shouldValidate: true,
                                                shouldDirty: true,
                                                shouldTouch: true,
                                            });
                                        }}
                                        isLoading={isFetchingGetFilterReferencesOptions}
                                    />
                                );
                            }}
                        />
                    </div>
                </div>
                {rowTypeValueFromForm === ROW_TYPE_KEYS.MILESTONE &&
                    activitiesFromReferencesCount > MIN_ACTIVITIES_FOR_WARNING && (
                        <Alert severity={'warning'}>
                            This selection will result in {activitiesFromReferencesCount} activities being added - are
                            you sure you want to do this?
                        </Alert>
                    )}
                <div className={classes.submitRow}>
                    <Button type={'button'} variant={'secondary'} onClick={onCancel} data-testid={'row-config-cancel'}>
                        Cancel
                    </Button>
                    <Button type={'button'} onClick={handleConfirmClick} data-testid={'row-config-submit'}>
                        {mode === 'add' ? 'Add' : 'Update'}
                    </Button>
                </div>
            </div>
        </div>
    );
};
